// =============================================================================
// Sales sheet — the nightly email's report, over any date range
// =============================================================================
// The director gets an itemised per-shop sheet by email every night, but only
// for today and month-to-date. This is the same sheet — same rows, same figures,
// same per-shop layout — with a from/to the owner picks, so "what did we sell
// over Easter week?" is answerable without waiting for a night to pass.
//
// The figures come from the same backend function that builds the email
// (computeSalesSheetPeriod), so the screen and the inbox can never disagree.
// The one deliberate difference is the Status column: the owner is usually here
// to ask "where is this order up to?", which the emailed sheet doesn't answer.

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, apiGet, getApiError } from '../../lib/api';
import { businessTodayIso, addDaysIso, formatBusinessDate } from '../../lib/dates';
import { PageScaffold } from '../../components/PageScaffold';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { StatusPill } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { cn } from '../../lib/cn';

interface Row {
  date: string;
  invoiceNo: string;
  status: string;
  orderId: string;
  orderNo: string;
  customerName: string;
  customerPhone: string;
  qty: number;
  item: string;
  dimension: string;
  unitPrice: string;
  amount: string;
  discountPct: string;
  discountAmount: string;
  paidTotal: string;
  balanceDue: string;
  modeOfPayment: string;
  expectedDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  firstLineOfOrder: boolean;
  /** This row's payment, or null when no payment sits on it. */
  paymentAmount: string | null;
  paymentReference: string;
  /** A row carrying only a second tender — no product behind it. */
  isPaymentOnlyRow: boolean;
}

interface ShopSheet {
  shopId: string;
  code: string;
  name: string;
  rows: Row[];
  totalAmount: string;
}

interface Totals {
  orders: number;
  itemsAmount: string;
  discountTotal: string;
  netSales: string;
  collected: string;
  cashDue: string;
  fullyPaid: { count: number; amount: string };
  partlyPaid: { count: number; collected: string; outstanding: string };
  unpaid: { count: number; outstanding: string };
  delivered: { count: number; amount: string };
  awaitingDelivery: { count: number; amount: string };
  byMethod: Array<{ method: string; label: string; count: number; amount: string }>;
}

interface Sheet {
  companyName: string;
  from: string;
  to: string;
  rangeLabel: string;
  shops: ShopSheet[];
  totalOrders: number;
  totalAmount: string;
  totals: Totals;
}

const PAYMENT_METHODS = [
  { value: '', label: 'Any method' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'juice', label: 'Juice' },
  { value: 'emtel', label: 'Emtel' },
  { value: 'myt', label: 'MYT' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_STATUSES = [
  { value: '', label: 'Any' },
  { value: 'paid', label: 'Fully paid' },
  { value: 'partial', label: 'Part-paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

const ORDER_STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'READY', label: 'Ready' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'COMPLETED', label: 'Completed' },
];

const DELIVERY = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Delivered' },
  { value: 'false', label: 'Not delivered' },
];

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'accent' | 'positive'> = {
  CONFIRMED: 'info',
  READY: 'accent',
  DELIVERED: 'positive',
  COMPLETED: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: 'Confirmed',
  READY: 'Ready',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
};

const shortDate = (iso: string | null): string =>
  formatBusinessDate(iso, { day: '2-digit', month: 'short' });

/** Quick ranges, expressed against the Mauritius business day. */
function presets(): Array<{ label: string; from: string; to: string }> {
  const today = businessTodayIso();
  const [y, m] = today.split('-').map(Number);
  const monthStart = `${y}-${String(m).padStart(2, '0')}-01`;
  const prevMonthStart = m === 1 ? `${y! - 1}-12-01` : `${y}-${String(m! - 1).padStart(2, '0')}-01`;
  const prevMonthEnd = addDaysIso(monthStart, -1);
  return [
    { label: 'Today', from: today, to: today },
    { label: 'Yesterday', from: addDaysIso(today, -1), to: addDaysIso(today, -1) },
    { label: 'Last 7 days', from: addDaysIso(today, -6), to: today },
    { label: 'Last 30 days', from: addDaysIso(today, -29), to: today },
    { label: 'This month', from: monthStart, to: today },
    { label: 'Last month', from: prevMonthStart, to: prevMonthEnd },
  ];
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">{label}</span>
      <select
        className="kc-input min-w-[9rem]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * One entry per order, its lines kept together.
 *
 * The API returns one row per order LINE, with the order-level fields
 * (customer, paid, balance, delivery) repeated on each. Folding regroups them
 * so an order is a single table row again — and, usefully, removes the need for
 * the `firstLineOfOrder` trick that blanks Paid/Balance on repeat rows.
 */
interface FoldedOrder {
  orderId: string;
  head: Row;
  lines: Row[];
  totalQty: number;
  totalAmount: number;
  totalDiscount: number;
}

function foldByOrder(rows: Row[]): FoldedOrder[] {
  const out: FoldedOrder[] = [];
  const index = new Map<string, FoldedOrder>();
  // Built by iteration rather than a groupBy so the orders keep the order the
  // server sent them in (confirmedAt ascending) instead of hash order.
  for (const r of rows) {
    let group = index.get(r.orderId);
    if (!group) {
      group = { orderId: r.orderId, head: r, lines: [], totalQty: 0, totalAmount: 0, totalDiscount: 0 };
      index.set(r.orderId, group);
      out.push(group);
    }
    group.lines.push(r);
    group.totalQty += r.qty;
    group.totalAmount += Number(r.amount);
    group.totalDiscount += Number(r.discountAmount);
  }
  return out;
}

/** Stacked cell — one line per product, so the columns stay row-aligned. */
function Stack({
  items,
  className,
}: {
  items: JSX.Element[];
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('space-y-1', className)}>
      {items.map((el, i) => (
        <div key={i} className="leading-5">
          {el}
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: JSX.Element }): JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-3">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

function Line({ label, value }: { label: ReactNode; value: JSX.Element }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-ink-3">{label}</span>
      {value}
    </div>
  );
}

export function SalesSheetPage(): JSX.Element {
  const today = businessTodayIso();
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const [downloading, setDownloading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [status, setStatus] = useState('');
  const [delivered, setDelivered] = useState('');
  // Folded is the default: an order is the unit people think in, and a
  // three-line order spread over three rows makes the sheet read as three
  // sales. Itemised stays one click away for anyone reconciling line by line.
  const [folded, setFolded] = useState(true);

  // Only send the filters that are set — an empty string would be rejected by
  // the server's deliberately strict filter validation.
  const filterParams: Record<string, string> = {
    ...(paymentMethod ? { paymentMethod } : {}),
    ...(paymentStatus ? { paymentStatus } : {}),
    ...(status ? { status } : {}),
    ...(delivered ? { delivered } : {}),
  };
  const activeFilterCount = Object.keys(filterParams).length;

  const sheet = useQuery({
    queryKey: ['sales-sheet-range', from, to, paymentMethod, paymentStatus, status, delivered],
    queryFn: () => apiGet<Sheet>('/dashboards/sales-sheet', { from, to, ...filterParams }),
  });

  const err = sheet.isError ? getApiError(sheet.error)?.message ?? 'Could not load that range.' : null;

  const downloadExcel = (): void => {
    void (async () => {
      setDownloading(true);
      try {
        const res = await api.get('/dashboards/sales-sheet.xlsx', {
          // Export exactly what is on screen, filters and all — a workbook that
          // silently included the rows you filtered out would be a trap.
          params: { from, to, ...filterParams },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(
          new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales-sheet-${from}-to-${to}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      } finally {
        setDownloading(false);
      }
    })();
  };

  const data = sheet.data;
  const shopsWithRows = (data?.shops ?? []).filter((s) => s.rows.length > 0);

  return (
    <PageScaffold
      title="Sales sheet"
      subtitle="Every order confirmed in the range — the same sheet emailed each night, over any dates you pick."
      width="wide"
      contentClassName="space-y-4"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex rounded border border-rule overflow-hidden">
            {([
              { value: true, label: 'One row per order' },
              { value: false, label: 'Itemised' },
            ] as const).map((o) => (
              <button
                key={o.label}
                type="button"
                onClick={() => setFolded(o.value)}
                className={cn(
                  'px-2.5 py-1.5 text-xs transition-colors',
                  folded === o.value
                    ? 'bg-accent-soft text-accent'
                    : 'text-ink-3 hover:text-ink hover:bg-paper-2',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <Button variant="secondary" onClick={downloadExcel} loading={downloading} disabled={!data}>
            Download Excel
          </Button>
        </div>
      }
    >
      <Card bodyClassName="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">From</span>
            <input type="date" className="kc-input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">To</span>
            <input type="date" className="kc-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </label>
          <div className="flex flex-wrap gap-1.5">
            {presets().map((p) => {
              const active = p.from === from && p.to === to;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    setFrom(p.from);
                    setTo(p.to);
                  }}
                  className={cn(
                    'px-2.5 py-1 rounded text-xs border transition-colors',
                    active
                      ? 'border-accent text-accent bg-accent-soft'
                      : 'border-rule-2 text-ink-3 hover:text-ink hover:border-rule',
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-rule">
          <Select label="Paid by" value={paymentMethod} onChange={setPaymentMethod} options={PAYMENT_METHODS} />
          <Select label="Payment" value={paymentStatus} onChange={setPaymentStatus} options={PAYMENT_STATUSES} />
          <Select label="Order status" value={status} onChange={setStatus} options={ORDER_STATUSES} />
          <Select label="Delivery" value={delivered} onChange={setDelivered} options={DELIVERY} />
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => {
                setPaymentMethod('');
                setPaymentStatus('');
                setStatus('');
                setDelivered('');
              }}
              className="px-2.5 py-1.5 rounded text-xs border border-rule-2 text-ink-3 hover:text-ink hover:border-rule transition-colors"
            >
              Clear {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </Card>

      {err && (
        <Card className="border-negative/40">
          <div className="text-sm text-negative">{err}</div>
        </Card>
      )}

      {data && !err && (
        <Card title="Report" subtitle={data.rangeLabel} bodyClassName="p-4 space-y-4">
          {/* The money line. Cash due is the figure people come here for, so it
              carries the emphasis and the warning tone when non-zero. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Stat label="Orders" value={<span className="kc-tnum text-ink text-lg">{data.totals.orders}</span>} />
            <Stat label="Net sales" value={<Money value={data.totals.netSales} className="text-ink text-lg" />} />
            <Stat label="Discount given" value={<Money value={data.totals.discountTotal} className="text-ink-3 text-lg" />} />
            <Stat label="Collected" value={<Money value={data.totals.collected} className="text-positive text-lg" />} />
            <Stat
              label="Cash due"
              value={
                <Money
                  value={data.totals.cashDue}
                  className={cn('text-lg', Number(data.totals.cashDue) > 0 ? 'text-warning' : 'text-ink-3')}
                />
              }
            />
          </div>

          {/* Where each order stands. The three payment buckets are mutually
              exclusive, so their counts add back up to Orders above. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 pt-3 border-t border-rule">
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3">Payment</div>
              <Line
                label={`Fully paid · ${data.totals.fullyPaid.count} order${data.totals.fullyPaid.count === 1 ? '' : 's'}`}
                value={<Money value={data.totals.fullyPaid.amount} className="text-positive" />}
              />
              {/* A part-paid order has two figures — what came in and what is
                  still owed. They get a line each: side by side they read as
                  one number split by a comma, and the eye can't scan a column
                  of them. */}
              <Line
                label={`Part-paid · ${data.totals.partlyPaid.count} order${data.totals.partlyPaid.count === 1 ? '' : 's'}`}
                value={<span className="text-ink-3 text-xs">—</span>}
              />
              <Line
                label={<span className="pl-3 text-ink-3">collected</span>}
                value={<Money value={data.totals.partlyPaid.collected} className="text-positive" />}
              />
              <Line
                label={<span className="pl-3 text-ink-3">still due</span>}
                value={<Money value={data.totals.partlyPaid.outstanding} className="text-warning" />}
              />
              <Line
                label={`Unpaid · ${data.totals.unpaid.count} order${data.totals.unpaid.count === 1 ? '' : 's'}`}
                value={<Money value={data.totals.unpaid.outstanding} className="text-negative" />}
              />
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3">Delivery</div>
              <Line
                label={`Delivered · ${data.totals.delivered.count} order${data.totals.delivered.count === 1 ? '' : 's'}`}
                value={<Money value={data.totals.delivered.amount} className="text-ink" />}
              />
              <Line
                label={`Awaiting delivery · ${data.totals.awaitingDelivery.count} order${data.totals.awaitingDelivery.count === 1 ? '' : 's'}`}
                value={<Money value={data.totals.awaitingDelivery.amount} className="text-ink-3" />}
              />
            </div>
          </div>

          {/* Actual money received per tender — from the payment rows, so this
              is what hit each drawer/account, not what the orders were worth. */}
          {data.totals.byMethod.length > 0 && (
            <div className="pt-3 border-t border-rule">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3 mb-2">
                Received by tender
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {data.totals.byMethod.map((m) => (
                  <div key={m.method} className="flex items-baseline gap-2 text-sm">
                    <span className="text-ink-3 text-xs">
                      {m.label}
                      <span className="text-ink-3/60"> ×{m.count}</span>
                    </span>
                    <Money value={m.amount} className="text-ink" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {sheet.isLoading && <Card><div className="text-sm text-ink-3">Loading…</div></Card>}

      {data && !err && shopsWithRows.length === 0 && (
        <EmptyState title="No confirmed sales in this range" description="Try a wider range or a different month." />
      )}

      {shopsWithRows.map((shop) => (
        <Card key={shop.shopId} title={`${shop.name} (${shop.code})`} bodyClassName="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Inv #</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 font-medium">Phone</th>
                  <th className="px-3 py-2 font-medium text-right">Qty</th>
                  <th className="px-3 py-2 font-medium">Item</th>
                  <th className="px-3 py-2 font-medium">Dimension</th>
                  <th className="px-3 py-2 font-medium text-right">Unit price</th>
                  <th className="px-3 py-2 font-medium text-right">Amount</th>
                  <th className="px-3 py-2 font-medium text-right">Discount</th>
                  <th className="px-3 py-2 font-medium text-right">Paid</th>
                  <th className="px-3 py-2 font-medium text-right">Balance</th>
                  <th className="px-3 py-2 font-medium">Payment</th>
                  <th className="px-3 py-2 font-medium">Exp. del.</th>
                  <th className="px-3 py-2 font-medium">Act. del.</th>
                </tr>
              </thead>
              <tbody>
                {/* ── Folded: one row per order, products stacked in place ── */}
                {folded &&
                  foldByOrder(shop.rows).map((o) => (
                    <tr key={o.orderId} className="border-b border-rule-2 align-top">
                      <td className="px-3 py-2 text-ink-3">{shortDate(o.head.date)}</td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/orders/${o.orderId}`}
                          className="kc-mono text-xs text-accent hover:underline"
                        >
                          {o.head.invoiceNo}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill tone={STATUS_TONE[o.head.status] ?? 'neutral'}>
                          {STATUS_LABEL[o.head.status] ?? o.head.status}
                        </StatusPill>
                      </td>
                      <td className="px-3 py-2 text-ink">{o.head.customerName}</td>
                      <td className="px-3 py-2 text-ink-3 kc-mono text-xs">
                        {o.head.customerPhone}
                      </td>
                      {/* Qty / Item / Dimension / Unit price / Amount stack one
                          entry per product. Same count and line-height in every
                          cell, so the five columns stay aligned across a row. */}
                      <td className="px-3 py-2 text-right kc-tnum">
                        <Stack items={o.lines.map((l) => <>{l.qty}</>)} />
                        {o.lines.length > 1 && (
                          <div className="mt-1 pt-1 border-t border-rule-2 font-medium text-ink">
                            {o.totalQty}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-ink">
                        <Stack items={o.lines.map((l) => <>{l.item}</>)} />
                      </td>
                      <td className="px-3 py-2 text-ink-3">
                        <Stack items={o.lines.map((l) => <>{l.dimension}</>)} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Stack items={o.lines.map((l) => <Money value={l.unitPrice} />)} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Stack items={o.lines.map((l) => <Money value={l.amount} />)} />
                        {o.lines.length > 1 && (
                          <div className="mt-1 pt-1 border-t border-rule-2 font-medium">
                            <Money value={o.totalAmount} className="text-ink" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-ink-3">
                        {o.totalDiscount === 0 ? '—' : <Money value={o.totalDiscount} />}
                      </td>
                      {/* Order-level figures, now shown plainly — folding removes
                          the need to blank them on repeat rows. */}
                      <td className="px-3 py-2 text-right">
                        {Number(o.head.paidTotal) === 0 ? (
                          <span className="text-ink-3">—</span>
                        ) : (
                          <Money value={o.head.paidTotal} />
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {Number(o.head.balanceDue) === 0 ? (
                          <span className="text-ink-3">—</span>
                        ) : (
                          <Money value={o.head.balanceDue} className="text-warning" />
                        )}
                      </td>
                      <td className="px-3 py-2 text-ink-3">{o.head.modeOfPayment}</td>
                      <td className="px-3 py-2 text-ink-3">
                        {shortDate(o.head.expectedDeliveryDate)}
                      </td>
                      <td className="px-3 py-2 text-ink-3">
                        {shortDate(o.head.actualDeliveryDate)}
                      </td>
                    </tr>
                  ))}

                {/* ── Itemised: one row per order line, as the printed sheet ── */}
                {!folded &&
                  shop.rows.map((r, i) => (
                  <tr
                    key={`${r.orderId}-${i}`}
                    className={cn(
                      'border-b border-rule-2',
                      // A rule above each new order keeps multi-line orders
                      // visually grouped, the way the printed sheet reads.
                      r.firstLineOfOrder && i > 0 && 'border-t border-rule',
                    )}
                  >
                    <td className="px-3 py-2 text-ink-3">{shortDate(r.date)}</td>
                    <td className="px-3 py-2">
                      <Link to={`/orders/${r.orderId}`} className="kc-mono text-xs text-accent hover:underline">
                        {r.invoiceNo}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill tone={STATUS_TONE[r.status] ?? 'neutral'}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </StatusPill>
                    </td>
                    <td className="px-3 py-2 text-ink">{r.customerName}</td>
                    <td className="px-3 py-2 text-ink-3 kc-mono text-xs">{r.customerPhone}</td>
                    {/* A payment-only row has no product behind it (see
                        isPaymentOnlyRow) — these cells stay empty rather than
                        printing a zero that reads as a free item. */}
                    <td className="px-3 py-2 text-right kc-tnum">{r.isPaymentOnlyRow ? '' : r.qty}</td>
                    <td className="px-3 py-2 text-ink">{r.item}</td>
                    <td className="px-3 py-2 text-ink-3">{r.dimension}</td>
                    <td className="px-3 py-2 text-right">
                      {r.isPaymentOnlyRow ? '' : <Money value={r.unitPrice} />}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.isPaymentOnlyRow ? '' : <Money value={r.amount} />}
                    </td>
                    <td className="px-3 py-2 text-right text-ink-3">
                      {r.isPaymentOnlyRow ? '' : Number(r.discountAmount) === 0 ? '—' : <Money value={r.discountAmount} />}
                    </td>
                    {/* Paid follows the PAYMENT on this row — a mixed-payment
                        order splits one tender per row. Balance is order-level
                        with no per-row equivalent, so it stays on the first row
                        only and isn't double-counted. Same rule as the CSV. */}
                    <td className="px-3 py-2 text-right">
                      {r.paymentAmount ? (
                        <Money value={r.paymentAmount} />
                      ) : r.firstLineOfOrder && Number(r.paidTotal) === 0 ? (
                        <span className="text-ink-3">—</span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!r.firstLineOfOrder ? (
                        ''
                      ) : Number(r.balanceDue) === 0 ? (
                        <span className="text-ink-3">—</span>
                      ) : (
                        <Money value={r.balanceDue} className="text-warning" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-3">
                      {r.modeOfPayment}
                      {r.paymentReference !== '—' && (
                        <span className="ml-1 kc-mono text-xs text-ink-3">{r.paymentReference}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-3">{shortDate(r.expectedDeliveryDate)}</td>
                    <td className="px-3 py-2 text-ink-3">{shortDate(r.actualDeliveryDate)}</td>
                  </tr>
                ))}
                <tr className="bg-paper-2">
                  <td className="px-3 py-2 font-medium text-ink" colSpan={9}>
                    Total
                  </td>
                  <td className="px-3 py-2 text-right font-medium">
                    <Money value={shop.totalAmount} className="text-ink" />
                  </td>
                  <td colSpan={6} />
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </PageScaffold>
  );
}
